import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clock,
  FileX,
  Loader2,
  Printer,
  RotateCcw,
  Search,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { useFiscalStatus } from '@/hooks/useFiscalStatus'
import { usePraInvoiceEnabled } from '@/hooks/usePraInvoiceEnabled'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
  formatDateHuman,
  formatDateTimeHuman,
  formatTimeHuman,
  toIsoDate,
  cn,
} from '@/lib/utils'
import { printPraTaxInvoice } from '@/lib/printPraTaxInvoice'
import {
  getCashierNameFromStorage,
  parseReceiptSettings,
  checkoutStoredIntentToReceiptPaymentMethod,
} from '@/lib/printCustomerReceipt'
import { printGuestReceiptForOrderId } from '@/lib/printGuestReceiptReplay'
import { printRefundReceipt } from '@/lib/printRefundReceipt'
import {
  displayLabelForOrderType,
  normalizeEnabledOrderTypes,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'
import { FiscalNoteDialog } from '@/components/admin/FiscalNoteDialog'
import { CreditNoteView } from '@/components/admin/CreditNoteView'
import type { Order, OrderItem, OrdersBrowserRow } from '@/types'

type PraFilter = 'all' | 'printed' | 'not_printed' | 'eligible'
type FiscalFilter = 'all' | 'synced' | 'pending' | 'failed' | 'none'

/** Empty seated-tab releases / void-all cancels — Rs 0, not credit notes. Noise in Daily Sales. */
function isEmptyCancelledNoise(o: OrdersBrowserRow): boolean {
  return o.status === 'cancelled' && Number(o.total_amount) === 0 && !o.cancelled_by_credit_note
}

const PRA_FILTERS: { id: PraFilter; label: string; hint: string }[] = [
  { id: 'all', label: 'All orders', hint: 'Every order on this day' },
  { id: 'not_printed', label: 'Not printed', hint: 'PRA invoice was never printed for these' },
  { id: 'printed', label: 'Printed', hint: 'A PRA invoice was already issued' },
  { id: 'eligible', label: 'Eligible to print', hint: 'You can issue / reissue a PRA invoice now' },
]

const FISCAL_FILTERS: { id: FiscalFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'synced', label: 'Synced' },
  { id: 'pending', label: 'Pending' },
  { id: 'failed', label: 'Failed' },
  { id: 'none', label: 'N/A' },
]

interface Props {
  /** Calendar day to load orders for (from Daily Sales drill-in + `DayFilter`). */
  day: Date
  /** When set (invoice-search deep-link), auto-open this order's drawer once
   *  the day's orders have loaded and contain it. */
  initialOrderId?: string
}

export function OrdersBrowserTab({ day, initialOrderId }: Props) {
  const { toast } = useToast()
  const { formatCurrency } = useCurrency()
  const queryClient = useQueryClient()
  // Gate PRA / FBR UI to the tenant's configured mode. PRA slip is governed by
  // app-setting pra_invoice_enabled; FBR (digital submission) by the fiscal
  // authority. Independent — a tenant can run one, both, or neither.
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()

  const [search, setSearch] = useState('')
  const [praFilter, setPraFilter] = useState<PraFilter>('all')
  const [fiscalFilter, setFiscalFilter] = useState<FiscalFilter>('all')
  // Empty open-tab releases flood Daily Sales (Rs 0 Cancelled). Hide by default;
  // toggle on for floor-audit of who released which table.
  const [showEmptyCancels, setShowEmptyCancels] = useState(false)
  const [printingId, setPrintingId] = useState<string | null>(null)
  const [printingGuestId, setPrintingGuestId] = useState<string | null>(null)
  const [printingRefundId, setPrintingRefundId] = useState<string | null>(null)
  const [drawerOrderId, setDrawerOrderId] = useState<string | null>(null)
  const [noteDialog, setNoteDialog] = useState<{ order: OrdersBrowserRow } | null>(null)
  const [viewCreditNoteId, setViewCreditNoteId] = useState<string | null>(null)

  const dayISO = toIsoDate(day)

  const ordersQuery = useQuery({
    queryKey: ['reports-v2', 'orders-browser', dayISO, search.trim(), praFilter, fiscalFilter],
    queryFn: () => apiClient.getOrdersBrowser(dayISO, {
      search: search.trim() || undefined,
      pra_filter: praFilter,
      fiscal_filter: fiscalFilter,
    }),
    staleTime: 15_000,
  })

  // Order-type config (built-ins + custom channels) so the Order Type column
  // resolves human labels — including custom names like "Foodpanda". Same
  // query key the checkout/dashboard use, so it's shared/cached.
  const { data: rawOrderTypes } = useQuery({
    queryKey: ['settings', 'enabled_order_types'],
    queryFn: async () => {
      const res = await apiClient.getSetting('enabled_order_types')
      return res.success && Array.isArray(res.data) ? res.data : []
    },
    staleTime: 60_000,
  })
  const orderTypeRows = useMemo(
    () => normalizeEnabledOrderTypes(rawOrderTypes ?? []),
    [rawOrderTypes],
  )

  const data = ordersQuery.data?.data
  const allOrders = data?.orders ?? []
  const emptyCancelCount = useMemo(
    () => allOrders.filter(isEmptyCancelledNoise).length,
    [allOrders],
  )
  const orders = useMemo(
    () => (showEmptyCancels ? allOrders : allOrders.filter((o) => !isEmptyCancelledNoise(o))),
    [allOrders, showEmptyCancels],
  )
  const windowDays = data?.pra_window_days ?? 7

  // Invoice-search deep-link: auto-open the requested order's drawer once it
  // appears in the loaded day. Guarded so it fires once — re-closing stays
  // closed, and a stale/unknown id is simply ignored. Search against allOrders
  // so a deep-link to an empty cancel still opens even when the noise filter is on.
  const autoOpenedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialOrderId || autoOpenedRef.current === initialOrderId) return
    if (allOrders.some((o) => o.id === initialOrderId)) {
      autoOpenedRef.current = initialOrderId
      setShowEmptyCancels(true)
      setDrawerOrderId(initialOrderId)
    }
  }, [initialOrderId, allOrders])

  const totals = useMemo(() => {
    // Gross always from completed rows in the full day payload — hiding empty
    // cancels must not change the money strip.
    return allOrders.reduce(
      (acc, o) => ({
        count: acc.count + 1,
        revenue: acc.revenue + (o.status === 'completed' ? o.total_amount : 0),
        printed: acc.printed + (o.pra_invoice_printed ? 1 : 0),
        credits: acc.credits + (o.cancelled_by_credit_note ? (o.credit_note_total ?? o.total_amount) : 0),
        creditsCount: acc.creditsCount + (o.cancelled_by_credit_note ? 1 : 0),
      }),
      { count: 0, revenue: 0, printed: 0, credits: 0, creditsCount: 0 },
    )
  }, [allOrders])

  // Net-of-credits view derived locally from the row data so the summary
  // strip ties out to the per-row strikethroughs (no extra round-trip).
  const netRevenue = totals.revenue - totals.credits
  const netOrders = totals.count - totals.creditsCount

  const printPra = useMutation({
    mutationFn: async (row: OrdersBrowserRow) => {
      // Always pull the full order (with items) before sending to the printer —
      // the OrdersBrowser row is intentionally lightweight. Routed through
      // queryClient so the OrderDrawer's in-card receipt view shares this
      // fetch (one network call per drawer-open + print, not two).
      const [orderRes, settingsRes, orderTypes] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['order-detail', row.id],
          queryFn: () => apiClient.getOrder(row.id),
        }),
        queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        }),
        queryClient.fetchQuery({
          queryKey: ['settings', 'enabled_order_types'],
          queryFn: async () => {
            const res = await apiClient.getSetting('enabled_order_types')
            if (res.success && Array.isArray(res.data)) return res.data as OrderTypeConfig[]
            return []
          },
        }),
      ])
      if (!orderRes.success || !orderRes.data) {
        throw new Error(orderRes.message || 'Failed to load order')
      }
      if (!settingsRes.success || !settingsRes.data) {
        throw new Error(settingsRes.message || 'Failed to load receipt settings')
      }

      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const order = orderRes.data
      const paidAt = order.completed_at ? new Date(order.completed_at) : new Date()
      const method = checkoutStoredIntentToReceiptPaymentMethod(order.checkout_payment_method)

      const { invoiceNumber, printed } = await printPraTaxInvoice(order, cfg, {
        cashierName: getCashierNameFromStorage(),
        paymentMethod: method,
        paidAt,
        orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypes),
        formatAmount: formatCurrency,
      })

      if (!printed) {
        throw new Error('Print dialog was cancelled')
      }

      // Recording the print is best-effort; failures here will surface on the
      // next refresh as the order keeps its existing state.
      await apiClient.markPraInvoicePrinted(order.id, invoiceNumber || undefined)
    },
    onSuccess: () => {
      toast({ title: 'PRA invoice printed', description: 'Audit log updated.' })
      queryClient.invalidateQueries({ queryKey: ['reports-v2', 'orders-browser'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (error: unknown) => {
      const msg =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : String(error)
      toast({
        title: 'Could not print PRA invoice',
        description: msg,
        variant: 'destructive',
      })
    },
    onSettled: () => setPrintingId(null),
  })

  const runPrintGuestReceipt = async (row: OrdersBrowserRow) => {
    setPrintingGuestId(row.id)
    try {
      const r = await printGuestReceiptForOrderId(apiClient, queryClient, row.id, {
        formatAmount: formatCurrency,
        getCashierName: getCashierNameFromStorage,
        isDuplicateCopy: true,
      })
      if (!r.ok) {
        toast({
          title: 'Could not print guest receipt',
          description: r.reason,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Guest receipt',
          description: 'Sent to print dialog.',
        })
      }
    } finally {
      setPrintingGuestId(null)
    }
  }

  // One-click reprint of the customer-facing refund receipt straight from the
  // credit-note sub-row — no need to open the CreditNoteView modal first.
  // Fetches the full fiscal note (the row only carries its id) plus receipt
  // branding, then dispatches the same printer the modal uses.
  const runPrintRefundReceipt = async (creditNoteId: string, orderNumber?: string) => {
    setPrintingRefundId(creditNoteId)
    try {
      const [noteRes, settingsRes] = await Promise.all([
        apiClient.getFiscalNote(creditNoteId),
        queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        }),
      ])
      if (!noteRes.success || !noteRes.data) {
        throw new Error(noteRes.message || 'Failed to load refund')
      }
      if (!settingsRes.success || !settingsRes.data) {
        throw new Error(settingsRes.message || 'Failed to load receipt settings')
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const { printed } = printRefundReceipt(noteRes.data, cfg, { orderNumber })
      if (!printed) throw new Error('Print dialog was cancelled')
      toast({ title: 'Refund receipt', description: 'Sent to print dialog.' })
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      toast({
        title: 'Could not print refund receipt',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setPrintingRefundId(null)
    }
  }

  const focusedOrder = useMemo(
    () => orders.find((o) => o.id === drawerOrderId) ?? null,
    [orders, drawerOrderId],
  )

  return (
    <div className="space-y-4">
      {/* Filter bar — calendar day is chosen in Daily Sales (drill / DayFilter). */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search order #, table, customer, server"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {praEnabled && (
            <div className="flex items-center gap-1 rounded-md bg-muted p-1">
              {PRA_FILTERS.map((f) => (
                <TooltipProvider key={f.id} delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant={praFilter === f.id ? 'default' : 'ghost'}
                        className="h-7 px-3 text-xs"
                        onClick={() => setPraFilter(f.id)}
                      >
                        {f.label}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">{f.hint}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          )}

          {fiscalEnabled && (
            <div className="flex items-center gap-1 rounded-md bg-muted p-1">
              {FISCAL_FILTERS.map((f) => (
                <Button
                  key={f.id}
                  type="button"
                  size="sm"
                  variant={fiscalFilter === f.id ? 'default' : 'ghost'}
                  className="h-7 px-3 text-xs"
                  onClick={() => setFiscalFilter(f.id)}
                >
                  FBR: {f.label}
                </Button>
              ))}
            </div>
          )}

          {emptyCancelCount > 0 && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant={showEmptyCancels ? 'default' : 'outline'}
                    className="h-7 px-3 text-xs"
                    onClick={() => setShowEmptyCancels((v) => !v)}
                  >
                    Empty cancels ({emptyCancelCount})
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-xs">
                  Rs 0 Cancelled rows from releasing empty seated tables (or void-all).
                  Hidden by default — they do not affect Gross Sales.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-[11px]">
              Late-print window: {windowDays} day{windowDays === 1 ? '' : 's'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base">
            Orders on {formatDateHuman(day)} ({orders.length}
            {!showEmptyCancels && emptyCancelCount > 0
              ? ` · ${emptyCancelCount} empty cancels hidden`
              : ''}
            )
          </CardTitle>
          <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-3 flex-wrap">
            <span>
              Gross: <span className="font-semibold text-foreground">{formatCurrency(totals.revenue)}</span>
            </span>
            {totals.credits > 0 ? (
              <>
                <span className="text-amber-700 dark:text-amber-400">
                  Credits: <span className="font-semibold">−{formatCurrency(totals.credits)}</span>{' '}
                  <span className="text-[11px]">({totals.creditsCount})</span>
                </span>
                <span>
                  Net (after credits):{' '}
                  <span className="font-semibold text-foreground">{formatCurrency(netRevenue)}</span>{' '}
                  <span className="text-[11px]">({netOrders} orders)</span>
                </span>
              </>
            ) : null}
            {praEnabled && (
              <span>
                PRA printed:{' '}
                <span className="font-semibold text-foreground">
                  {totals.printed}/{totals.count}
                </span>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ordersQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : ordersQuery.isError ? (
            <div className="p-10 text-center text-sm text-destructive">
              Failed to load orders: {String((ordersQuery.error as Error)?.message ?? 'unknown')}
            </div>
          ) : orders.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No orders match these filters for {formatDateHuman(day)}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] md:min-w-[840px] lg:min-w-[1000px] text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2.5">Order #</th>
                    <th className="text-left px-4 py-2.5">Time</th>
                    <th className="hidden md:table-cell text-left px-4 py-2.5">Table</th>
                    <th className="hidden lg:table-cell text-left px-4 py-2.5">Server</th>
                    <th className="hidden lg:table-cell text-right px-4 py-2.5">Party</th>
                    <th className="text-right px-4 py-2.5">Total</th>
                    <th className="hidden md:table-cell text-left px-4 py-2.5">Payment</th>
                    <th className="hidden lg:table-cell text-left px-4 py-2.5">Order Type</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    {praEnabled && <th className="hidden md:table-cell text-left px-4 py-2.5">PRA</th>}
                    {fiscalEnabled && <th className="hidden md:table-cell text-left px-4 py-2.5">FBR</th>}
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map((row) => (
                    <OrdersRow
                      key={row.id}
                      row={row}
                      orderTypeRows={orderTypeRows}
                      printingPra={printingId === row.id}
                      printingGuest={printingGuestId === row.id}
                      formatCurrency={formatCurrency}
                      onView={() => setDrawerOrderId(row.id)}
                      onPrintPra={() => {
                        setPrintingId(row.id)
                        printPra.mutate(row)
                      }}
                      onPrintGuest={() => {
                        void runPrintGuestReceipt(row)
                      }}
                      onCreditNote={() => setNoteDialog({ order: row })}
                      onViewCreditNote={(id) => setViewCreditNoteId(id)}
                      printingRefund={
                        !!row.credit_note_id && printingRefundId === row.credit_note_id
                      }
                      onPrintRefundReceipt={(id) =>
                        void runPrintRefundReceipt(id, row.order_number)
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <OrderDrawer
        currentOrder={focusedOrder}
        formatCurrency={formatCurrency}
        onClose={() => setDrawerOrderId(null)}
        onPrintPra={() => {
          if (!focusedOrder) return
          setPrintingId(focusedOrder.id)
          printPra.mutate(focusedOrder)
        }}
        onPrintGuest={() => {
          if (!focusedOrder) return
          void runPrintGuestReceipt(focusedOrder)
        }}
        printingPra={!!focusedOrder && printingId === focusedOrder.id}
        printingGuest={!!focusedOrder && printingGuestId === focusedOrder.id}
        onCreditNote={() => focusedOrder && setNoteDialog({ order: focusedOrder })}
        onViewCreditNote={(id) => setViewCreditNoteId(id)}
      />

      {viewCreditNoteId && (
        <CreditNoteView
          noteId={viewCreditNoteId}
          orderNumber={focusedOrder?.order_number}
          onClose={() => setViewCreditNoteId(null)}
        />
      )}

      {noteDialog && (
        <FiscalNoteDialog
          order={noteDialog.order}
          noteType={'credit'}
          onClose={() => setNoteDialog(null)}
          onSuccess={(note) => {
            toast({
              title: fiscalEnabled ? 'Credit note issued' : 'Refund issued',
              description: note.fiscal_details?.irn
                ? `IRN ${note.fiscal_details.irn}`
                : `Status: ${note.status}`,
            })
            queryClient.invalidateQueries({ queryKey: ['reports-v2', 'orders-browser'] })
            setNoteDialog(null)
            setViewCreditNoteId(note.id)
          }}
        />
      )}
    </div>
  )
}

function OrdersRow({
  row,
  orderTypeRows,
  printingPra,
  printingGuest,
  formatCurrency,
  onView,
  onPrintPra,
  onPrintGuest,
  onCreditNote,
  onViewCreditNote,
  printingRefund,
  onPrintRefundReceipt,
}: {
  row: OrdersBrowserRow
  orderTypeRows: OrderTypeConfig[]
  printingPra: boolean
  printingGuest: boolean
  formatCurrency: (n: number) => string
  onView: () => void
  onPrintPra: () => void
  onPrintGuest: () => void
  onCreditNote: () => void
  onViewCreditNote: (id: string) => void
  printingRefund: boolean
  onPrintRefundReceipt: (id: string) => void
}) {
  // Backend ships a pre-formatted DD-MM-YYYY HH:mm `created_at_label`, but
  // the column header reads "Time" — show only the 12-hour clock so the
  // table doesn't repeat the day already in the section heading.
  const timePart = formatTimeHuman(row.created_at)
  const isSynced = row.fiscal_status === 'SYNCED'
  const isCancelledByCN = !!row.cancelled_by_credit_note
  const hasLinkedCreditNote = !!row.credit_note_id
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()
  // Non-fiscal tenants never reach fiscal_status === 'SYNCED' (there's no
  // FBR submission step), so the refund entry point falls back to the
  // order's own completion state. Backend handlers/fiscal_notes.go gates
  // the non-fiscal branch on the same `status='completed'` predicate.
  const canIssueCredit = fiscalEnabled ? isSynced : row.status === 'completed'

  return (
    <>
      <tr
        className={cn(
          'hover:bg-muted/20',
          isCancelledByCN && 'opacity-60 bg-muted/10',
        )}
      >
        <td className={cn('px-4 py-3 font-medium tabular-nums', isCancelledByCN && 'line-through')}>
          {row.order_number}
        </td>
        <td className="px-4 py-3 text-muted-foreground tabular-nums">{timePart}</td>
        <td className="hidden md:table-cell px-4 py-3">{row.table_number ?? '—'}</td>
        <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">{row.server_name ?? '—'}</td>
        <td className="hidden lg:table-cell text-right tabular-nums px-4 py-3">{row.guest_count > 0 ? row.guest_count : '—'}</td>
        <td className={cn('text-right tabular-nums px-4 py-3 font-semibold', isCancelledByCN && 'line-through')}>
          {formatCurrency(row.total_amount)}
        </td>
        <td className="hidden md:table-cell px-4 py-3 text-muted-foreground capitalize">{row.checkout_payment_method ?? '—'}</td>
        <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">
          {row.order_type ? displayLabelForOrderType(row.order_type, orderTypeRows) : '—'}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={row.status} />
          {isCancelledByCN && (
            <Badge
              variant="outline"
              className="ml-1 text-[10px] gap-1 border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950 dark:text-red-200"
            >
              CN issued
            </Badge>
          )}
        </td>
        {praEnabled && (
          <td className="hidden md:table-cell px-4 py-3">
            <PraStatusBadge row={row} />
          </td>
        )}
        {fiscalEnabled && (
          <td className="hidden md:table-cell px-4 py-3">
            <FiscalStatusBadge status={row.fiscal_status} />
          </td>
        )}
        <td className="px-4 py-3 text-right">
          <div className="inline-flex flex-wrap items-center justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={onView}>
              View
            </Button>
            <GuestReceiptPrintButton
              row={row}
              printing={printingGuest}
              onClick={onPrintGuest}
            />
            {praEnabled && (
              <PrintPraButton row={row} printing={printingPra} onClick={onPrintPra} />
            )}
            {canIssueCredit && !isCancelledByCN && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 text-xs border-red-200 text-red-700 hover:bg-red-50"
                onClick={onCreditNote}
              >
                <ShieldAlert className="w-3.5 h-3.5" /> Cancel / {fiscalEnabled ? 'Credit Note' : 'Refund'}
              </Button>
            )}
          </div>
        </td>
      </tr>
      {hasLinkedCreditNote && (
        <tr className="bg-red-50/40 dark:bg-red-950/10 border-l-4 border-l-red-500">
          <td className="px-4 py-2 text-[11px] font-mono text-red-700 dark:text-red-300">
            <span className="inline-flex items-center gap-1">
              <FileX className="w-3 h-3" /> CN → {row.credit_note_irn ?? row.credit_note_id?.slice(0, 8) ?? 'pending'}
            </span>
          </td>
          <td className="px-4 py-2 text-[11px] text-muted-foreground tabular-nums">
            {row.credit_note_issued_at_label ?? '—'}
          </td>
          <td colSpan={3} className="hidden md:table-cell px-4 py-2 text-[11px] text-red-700 dark:text-red-300 italic">
            {row.credit_note_reason ? `Reason: ${row.credit_note_reason}` : 'No reason recorded'}
          </td>
          <td className="text-right tabular-nums px-4 py-2 font-semibold text-red-700 dark:text-red-300">
            −{formatCurrency(row.credit_note_total ?? row.total_amount)}
          </td>
          <td colSpan={4} className="hidden lg:table-cell px-4 py-2 text-[11px] text-muted-foreground">
            {row.credit_note_issued_by_name ? `by ${row.credit_note_issued_by_name}` : ''}
          </td>
          <td className="px-4 py-2 text-right">
            {row.credit_note_id && (
              <div className="inline-flex items-center justify-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-[11px] h-7 text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30"
                  disabled={printingRefund}
                  onClick={() => onPrintRefundReceipt(row.credit_note_id as string)}
                >
                  {printingRefund ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3 h-3 mr-1" />
                  )}
                  Refund receipt
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-[11px] h-7 text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30"
                  onClick={() => onViewCreditNote(row.credit_note_id as string)}
                >
                  View CN
                </Button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function StatusBadge({ status }: { status: string }) {
  let cls = 'bg-muted text-muted-foreground'
  if (status === 'completed') cls = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
  else if (status === 'cancelled') cls = 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
  else if (status === 'pending' || status === 'preparing') cls = 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
  else if (status === 'out_for_delivery') cls = 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'
  return <Badge variant="outline" className={cn('text-[10px] capitalize border-transparent', cls)}>{status.replace(/_/g, ' ')}</Badge>
}

function PraStatusBadge({ row }: { row: OrdersBrowserRow }) {
  if (!row.pra_invoice_printed) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950 dark:text-amber-200">
        <XCircle className="w-3 h-3" /> Not printed
      </Badge>
    )
  }
  if (row.can_print_pra) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950 dark:text-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> Printed
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
      <Clock className="w-3 h-3" /> Window expired
    </Badge>
  )
}

function FiscalStatusBadge({ status }: { status?: string | null }) {
  if (!status) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
        N/A
      </Badge>
    )
  }
  switch (status) {
    case 'SYNCED':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="w-3 h-3" /> Synced
        </Badge>
      )
    case 'PENDING':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950 dark:text-amber-200">
          <Clock className="w-3 h-3" /> Pending
        </Badge>
      )
    case 'FAILED':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950 dark:text-red-200">
          <XCircle className="w-3 h-3" /> Failed
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
          {status}
        </Badge>
      )
  }
}

function GuestReceiptPrintButton({
  row,
  printing,
  onClick,
}: {
  row: OrdersBrowserRow
  printing: boolean
  onClick: () => void
}) {
  const disabled = row.status !== 'completed' || printing
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className="gap-1.5 border-slate-400/70 text-slate-800 hover:bg-slate-50 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-900/40"
    >
      {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
      Guest receipt
    </Button>
  )
}

function PrintPraButton({
  row,
  printing,
  onClick,
}: {
  row: OrdersBrowserRow
  printing: boolean
  onClick: () => void
}) {
  const Icon = row.pra_invoice_printed ? RotateCcw : Printer
  const label = row.pra_invoice_printed ? 'Reprint PRA' : 'Print PRA'
  const button = (
    <Button
      type="button"
      size="sm"
      variant={row.can_print_pra ? 'default' : 'outline'}
      disabled={!row.can_print_pra || printing}
      onClick={onClick}
      className="gap-1.5"
    >
      {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </Button>
  )

  if (row.can_print_pra) return button

  const reason = row.can_print_pra_reason ?? 'PRA reprint not available for this order.'
  const printedNote = row.pra_invoice_printed_at_label
    ? ` Originally printed ${row.pra_invoice_printed_at_label}.`
    : ''
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs">
          {reason}{printedNote}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function OrderDrawer({
  currentOrder,
  formatCurrency,
  onClose,
  onPrintPra,
  onPrintGuest,
  printingPra,
  printingGuest,
  onCreditNote,
  onViewCreditNote,
}: {
  currentOrder: OrdersBrowserRow | null
  formatCurrency: (n: number) => string
  onClose: () => void
  onPrintPra: () => void
  onPrintGuest: () => void
  printingPra: boolean
  printingGuest: boolean
  onCreditNote: () => void
  onViewCreditNote: (id: string) => void
}) {
  // Hold the most recently focused order so the slide-out animation has
  // content to render while Radix is still tearing the panel down. Without
  // this the drawer would flash empty for ~200ms when the user closes it.
  const [lastOrder, setLastOrder] = useState<OrdersBrowserRow | null>(currentOrder)
  useEffect(() => {
    if (currentOrder) setLastOrder(currentOrder)
  }, [currentOrder])
  const order = currentOrder ?? lastOrder
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()
  // Mirror the table-row gate: non-fiscal tenants fall back to order status
  // because there is no fiscal_status === 'SYNCED' transition for them.
  const canIssueCredit = order
    ? fiscalEnabled
      ? order.fiscal_status === 'SYNCED'
      : order.status === 'completed'
    : false

  // Shared cache key with the printPra mutation above — opening the drawer
  // pre-warms the same cache the print path reads, so click-to-print after
  // viewing does zero extra round-trips.
  const orderDetailQuery = useQuery({
    queryKey: ['order-detail', order?.id],
    queryFn: () => apiClient.getOrder(order!.id),
    enabled: !!currentOrder && !!order?.id,
    staleTime: 60_000,
  })
  const fullOrder =
    orderDetailQuery.data?.success && orderDetailQuery.data.data
      ? orderDetailQuery.data.data
      : null
  return (
    <Sheet
      open={!!currentOrder}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent size="lg" className="p-0">
        {order && (
          <>
            <SheetHeader>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Order detail
              </p>
              <SheetTitle className="tabular-nums">{order.order_number}</SheetTitle>
              <p className="text-xs text-muted-foreground">
                Created {formatDateTimeHuman(order.created_at)}
              </p>
            </SheetHeader>
            <SheetBody className="space-y-4">
              <DetailRow label="Status">
                <StatusBadge status={order.status} />
              </DetailRow>
              <DetailRow label="Table">{order.table_number ?? '—'}</DetailRow>
              <DetailRow label="Server">{order.server_name ?? '—'}</DetailRow>
              <DetailRow label="Customer">{order.customer_name ?? '—'}</DetailRow>
              <DetailRow label="Party size">
                {order.guest_count > 0 ? order.guest_count : '—'}
              </DetailRow>
              <DetailRow label="Payment method">{order.checkout_payment_method ?? '—'}</DetailRow>
              <DetailRow label="Total">
                <span className="font-semibold">{formatCurrency(order.total_amount)}</span>
              </DetailRow>
              {order.completed_at_label && (
                <DetailRow label="Completed at">{order.completed_at_label}</DetailRow>
              )}

              <OrderReceiptCard
                rowOrder={order}
                fullOrder={fullOrder}
                isLoading={orderDetailQuery.isLoading}
                isError={orderDetailQuery.isError}
                onRetry={() => orderDetailQuery.refetch()}
                formatCurrency={formatCurrency}
              />

              <div className="rounded-md border border-border bg-muted/20 p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Thermal guest receipt
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Informational reprint only — does not update PRA or fiscal records.
                </p>
                <GuestReceiptPrintButton row={order} printing={printingGuest} onClick={onPrintGuest} />
              </div>

              {praEnabled && (
              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  PRA tax invoice
                </p>
                <DetailRow label="Status">
                  <PraStatusBadge row={order} />
                </DetailRow>
                {order.pra_invoice_number && (
                  <DetailRow label="Invoice #">{order.pra_invoice_number}</DetailRow>
                )}
                {order.pra_invoice_printed_at_label && (
                  <DetailRow label="First printed">{order.pra_invoice_printed_at_label}</DetailRow>
                )}
                {order.pra_invoice_reprint_count > 0 && (
                  <DetailRow label="Reprints">
                    {order.pra_invoice_reprint_count}
                    {order.pra_invoice_last_reprinted_by_name
                      ? ` (last by ${order.pra_invoice_last_reprinted_by_name})`
                      : ''}
                    {order.pra_invoice_last_reprinted_at
                      ? ` on ${formatDateTimeHuman(order.pra_invoice_last_reprinted_at)}`
                      : ''}
                  </DetailRow>
                )}
                {order.pra_late_window_expires_at && (
                  <DetailRow label="Eligible until">
                    {formatDateTimeHuman(order.pra_late_window_expires_at)}
                  </DetailRow>
                )}
                <div className="pt-3">
                  <PrintPraButton row={order} printing={printingPra} onClick={onPrintPra} />
                  {!order.can_print_pra && order.can_print_pra_reason && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {order.can_print_pra_reason}
                    </p>
                  )}
                </div>
              </div>
              )}

              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {fiscalEnabled ? 'FBR fiscal' : 'Refund'}
                </p>
                {fiscalEnabled && (
                  <>
                    <DetailRow label="Status">
                      <FiscalStatusBadge status={order.fiscal_status} />
                    </DetailRow>
                    {order.fiscal_irn && <DetailRow label="IRN">{order.fiscal_irn}</DetailRow>}
                    {order.fiscal_authority && (
                      <DetailRow label="Authority">{order.fiscal_authority}</DetailRow>
                    )}
                  </>
                )}
                {canIssueCredit && !order.cancelled_by_credit_note && (
                  <div className="flex gap-2 pt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs border-red-200 text-red-700 hover:bg-red-50"
                      onClick={onCreditNote}
                    >
                      <ShieldAlert className="w-3.5 h-3.5" /> Cancel / Issue {fiscalEnabled ? 'Credit Note' : 'Refund'}
                    </Button>
                  </div>
                )}
                {order.cancelled_by_credit_note && (
                  <div className="rounded-md border border-red-200 bg-red-50/50 dark:bg-red-950/20 p-3 mt-3 space-y-1">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                      {fiscalEnabled ? 'Cancelled by Credit Note' : 'Refunded'}
                    </p>
                    {order.credit_note_irn && (
                      <DetailRow label="CN IRN">{order.credit_note_irn}</DetailRow>
                    )}
                    {order.credit_note_total != null && (
                      <DetailRow label="CN total">
                        <span className="text-red-700 dark:text-red-300">
                          −{formatCurrency(order.credit_note_total)}
                        </span>
                      </DetailRow>
                    )}
                    {order.credit_note_issued_at_label && (
                      <DetailRow label="Issued">{order.credit_note_issued_at_label}</DetailRow>
                    )}
                    {order.credit_note_issued_by_name && (
                      <DetailRow label="By">{order.credit_note_issued_by_name}</DetailRow>
                    )}
                    {order.credit_note_reason && (
                      <DetailRow label="Reason">{order.credit_note_reason}</DetailRow>
                    )}
                    {order.credit_note_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 gap-1 text-xs"
                        onClick={() => onViewCreditNote(order.credit_note_id as string)}
                      >
                        View {fiscalEnabled ? 'Credit Note' : 'Refund'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{children}</span>
    </div>
  )
}

/**
 * On-screen mini receipt rendered inside the OrderDrawer. Surfaces the items
 * and the totals tally without needing the operator to print a guest receipt.
 *
 * Source-of-truth for ordering and conditional rendering of totals is
 * `lib/printCustomerReceipt.ts` — we match its row order so the screen and
 * the printed slip agree. The one intentional divergence is voided lines:
 * the printer hides them; this card keeps them visible but struck-through
 * because the audit-style review view benefits from seeing what was
 * rung up and reversed.
 */
function OrderReceiptCard({
  rowOrder,
  fullOrder,
  isLoading,
  isError,
  onRetry,
  formatCurrency,
}: {
  rowOrder: OrdersBrowserRow
  fullOrder: Order | null
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  formatCurrency: (n: number) => string
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border p-4 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <div className="space-y-2 pt-1">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-md border p-4 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Couldn't load items.</span>
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // No items at all — usually means an open tab that never had a KOT fire.
  // Hide the whole card so we don't render an empty Receipt header.
  if (!fullOrder || !fullOrder.items || fullOrder.items.length === 0) return null

  const items = fullOrder.items
  const liveItems = items.filter((i) => i.status !== 'voided')
  const totalQty = liveItems.reduce((s, i) => s + i.quantity, 0)
  const cancelledByCN = !!rowOrder.cancelled_by_credit_note

  const subtotal = fullOrder.subtotal ?? 0
  const taxAmount = fullOrder.tax_amount ?? 0
  const itemDiscounts = fullOrder.item_discounts_total ?? 0
  const billDiscount = fullOrder.discount_amount ?? 0
  const billDiscountPct = fullOrder.discount_percent
  const deliveryFee = fullOrder.delivery_fee_amount ?? 0
  const total = fullOrder.total_amount ?? 0

  const duration = formatOrderDuration(fullOrder.created_at, fullOrder.completed_at)

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">
          {fullOrder.order_type}
          {duration ? ` · ${duration}` : ''}
        </p>
      </div>

      <div className={cn('space-y-3', cancelledByCN && 'opacity-60')}>
        <ul className="divide-y divide-border/60">
          {items.map((item) => (
            <ReceiptItemRow key={item.id} item={item} formatCurrency={formatCurrency} />
          ))}
        </ul>

        <div className="border-t pt-2 space-y-1 text-sm">
          <TallyRow label="Total qty" value={totalQty.toLocaleString('en-US')} />
          <TallyRow label="Subtotal" value={formatCurrency(subtotal)} />
          <TallyRow label="Sales tax" value={formatCurrency(taxAmount)} />
          {itemDiscounts > 0 && (
            <TallyRow
              label="Item discounts"
              value={`−${formatCurrency(itemDiscounts)}`}
              muted
            />
          )}
          {billDiscount > 0 && (
            <TallyRow
              label={
                typeof billDiscountPct === 'number' && billDiscountPct > 0
                  ? `Discount (${billDiscountPct}%)`
                  : 'Discount'
              }
              value={`−${formatCurrency(billDiscount)}`}
              muted
            />
          )}
          {deliveryFee > 0 && (
            <TallyRow label="Delivery fee" value={formatCurrency(deliveryFee)} />
          )}
          <div className="flex items-baseline justify-between gap-3 border-t pt-2 mt-1 text-sm">
            <span className="font-semibold uppercase tracking-wide">Total</span>
            <span
              className={cn(
                'font-semibold tabular-nums',
                cancelledByCN && 'line-through',
              )}
            >
              {formatCurrency(total)}
            </span>
          </div>
          {cancelledByCN && (
            <div className="flex items-baseline justify-between gap-3 text-xs text-red-700 dark:text-red-300">
              <span>Cancelled by {rowOrder.credit_note_irn ? 'CN' : 'refund'}</span>
              <span className="tabular-nums">
                −{formatCurrency(rowOrder.credit_note_total ?? total)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ReceiptItemRow({
  item,
  formatCurrency,
}: {
  item: OrderItem
  formatCurrency: (n: number) => string
}) {
  const voided = item.status === 'voided'
  const name = item.product?.name ?? 'Item'
  const note = (item.special_instructions || item.notes || '').trim()
  const lineDiscount = item.discount_amount ?? 0
  const linePct = item.discount_percent
  return (
    <li className={cn('py-2 text-sm', voided && 'line-through opacity-60')}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="tabular-nums text-muted-foreground shrink-0">
          {item.quantity} ×
        </span>
        <span className="flex-1 break-words">{name}</span>
        <span className="tabular-nums shrink-0">{formatCurrency(item.total_price)}</span>
      </div>
      {note && (
        <p className="pl-7 text-xs text-muted-foreground">↳ {note}</p>
      )}
      {lineDiscount > 0 && (
        <p className="pl-7 text-xs text-muted-foreground">
          Discount
          {typeof linePct === 'number' && linePct > 0 ? ` (${linePct}%)` : ''}: −
          {formatCurrency(lineDiscount)}
        </p>
      )}
    </li>
  )
}

function TallyRow({
  label,
  value,
  muted,
}: {
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-xs', muted ? 'text-muted-foreground' : 'text-foreground')}>
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums text-sm',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  )
}

// Inline duration formatter — no shared util exists for "Xm Ys" style.
// Returns null when the order isn't completed (or the math is degenerate)
// so the header strip falls back to just the order_type chip.
function formatOrderDuration(createdAt?: string, completedAt?: string): string | null {
  if (!createdAt || !completedAt) return null
  const start = new Date(createdAt).getTime()
  const end = new Date(completedAt).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const secs = Math.round((end - start) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rem = secs % 60
  if (mins < 60) return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`
  const hrs = Math.floor(mins / 60)
  const minsRem = mins % 60
  return minsRem === 0 ? `${hrs}h` : `${hrs}h ${minsRem}m`
}

